import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation } from "wouter";
import {
  Activity,
  Bell,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Cpu,
  FileClock,
  GalleryHorizontalEnd,
  LayoutDashboard,
  Library,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
  RefreshCw,
  WifiOff,
  X,
} from "lucide-react";
import { useAuth } from "./auth";
import { useShellStatus } from "./pwa";

const links = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/devices", label: "Devices", icon: Cpu },
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/galleries", label: "Galleries", icon: GalleryHorizontalEnd },
  { to: "/workflows", label: "Workflows", icon: Library },
  { to: "/deployments", label: "Deployments", icon: FileClock },
  { to: "/software-updates", label: "Software updates", icon: RefreshCw },
  { to: "/analytics", label: "Analytics", icon: Activity },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/settings", label: "Settings", icon: Settings },
];

type ThemeContextValue = {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
};
const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used inside AppLayout.");
  return context;
};

export const AppLayout = ({ children }: { children: ReactNode }) => {
  const { user, signOutUser } = useAuth();
  const [rail, setRail] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">(() =>
    (localStorage.getItem("booth-control-centre-theme") ??
      localStorage.getItem("ai-booth-theme")) === "dark"
      ? "dark"
      : "light",
  );
  const [location] = useLocation();
  const { isOffline } = useShellStatus();
  const themeValue = useMemo(() => ({ theme, setTheme }), [theme]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("booth-control-centre-theme", theme);
    localStorage.setItem("ai-booth-theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!drawer) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawer(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [drawer]);

  return (
    <ThemeContext.Provider value={themeValue}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div className={`app-shell ${rail ? "is-rail" : ""}`}>
        <header className="mobile-topbar">
          <button
            className="mobile-menu"
            aria-label={drawer ? "Close navigation" : "Open navigation"}
            aria-expanded={drawer}
            aria-controls="dashboard-navigation"
            onClick={() => setDrawer((isOpen) => !isOpen)}
          >
            {drawer ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="mobile-topbar-brand" aria-label="BoothWorks Control Centre">
            <span className="mobile-topbar-mark" aria-hidden="true">
              <img src="/boothworks-pp.jpg" alt="" />
            </span>
            <span className="mobile-topbar-copy">
              <strong>BoothWorks</strong>
              <span>Control Centre</span>
            </span>
          </div>
          {isOffline && (
            <span className="shell-status is-offline" role="status" aria-live="polite">
              <WifiOff size={15} aria-hidden="true" />
              <span>Offline</span>
            </span>
          )}
        </header>
        {drawer && (
          <button
            className="drawer-scrim"
            aria-label="Close navigation"
            onClick={() => setDrawer(false)}
          />
        )}
        <aside
          id="dashboard-navigation"
          className={`sidebar ${drawer ? "is-open" : ""}`}
        >
          <div className="brand">
            <div className="brand-mark">
              <img src="/boothworks-pp.jpg" alt="" />
            </div>
            <div className="brand-copy">
              <strong>BoothWorks</strong>
              <span>Control Centre</span>
            </div>
            <button
              className="mobile-close"
              onClick={() => setDrawer(false)}
              aria-label="Close navigation"
            >
              <X size={19} />
            </button>
          </div>
          <nav aria-label="Main navigation">
            {links.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                href={to}
                className={location === to ? "active" : ""}
                aria-label={label}
                title={label}
                onClick={() => setDrawer(false)}
              >
                <Icon size={18} aria-hidden="true" />
                <span>{label}</span>
              </Link>
            ))}
          </nav>
          <div className="sidebar-foot">
            <button
              className="theme-toggle"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              aria-label={`Use ${theme === "light" ? "dark" : "light"} theme`}
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              <span>{theme === "light" ? "Dark theme" : "Light theme"}</span>
            </button>
            <div className="account">
              <div className="avatar">
                {user?.email?.slice(0, 1).toUpperCase() || "O"}
              </div>
              <div>
                <strong>{user?.displayName || "Owner"}</strong>
                <span>{user?.email}</span>
              </div>
              <button onClick={() => void signOutUser()} aria-label="Sign out">
                <LogOut size={18} />
              </button>
            </div>
          </div>
          <button
            className="rail-toggle"
            onClick={() => setRail(!rail)}
            aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
          >
            {rail ? <ChevronRight size={17} /> : <ChevronLeft size={17} />}
          </button>
        </aside>
        <main id="main-content">{children}</main>
      </div>
    </ThemeContext.Provider>
  );
};

export const PageHeader = ({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) => (
  <header className="page-header">
    <div>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h1>{title}</h1>
      <p>{description}</p>
    </div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>
);
